import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import type {
  TicketDetail,
  TicketDetailArtifact,
  TicketDetailReviewCriterion,
  TicketMergeReadiness,
} from '@nightshift/ui-types';

import { EvidenceImage } from '../../components/evidence-image.tsx';
import { Spinner } from '../../components/primitives.tsx';
import { api } from '../../live/api.ts';
import { groupReviewWaves, summariseWave, type ReviewWave } from '../../lib/review-waves.ts';
import { isEvidenceImage } from './evidence.tsx';
import { ReviewCard, ReviewWaveSection } from './review.tsx';
import './review-merge.css';

/** Artifact kinds that are already shown elsewhere in this panel or are bookkeeping. */
const HIDDEN_ARTIFACT_KINDS = new Set([
  'ai-review-verdict',
  'ai-review-findings',
  'ai-review-spec-dispute',
  'verification-profile',
  'mr-diff-summary',
  'policy-approval',
]);

function fileName(filePath: string | null): string {
  return filePath?.split(/[\\/]/).pop() ?? '';
}

type AcceptanceCriterionSummary = TicketDetailReviewCriterion;

const CRITERION_STATUS_LABEL: Record<AcceptanceCriterionSummary['status'], string> = {
  met: 'Met',
  'not-met': 'Not met',
  unproven: 'Unable to verify',
  'not-checked': 'Not checked this round',
  'not-assessed': 'Assessment not reported',
};

/**
 * Collapse the newest review wave into one conservative acceptance checklist. A criterion
 * is met only when every non-cancelled reviewer in the wave says so; one negative or missing assessment
 * remains visible instead of being hidden behind another reviewer's pass.
 */
export function summariseAcceptanceCriteria(wave: ReviewWave | undefined): AcceptanceCriterionSummary[] {
  if (!wave || wave.reviews.length === 0) return [];
  // A cancelled attempt did not assess the head. Keep it visible in the review history,
  // but do not let its missing assessment turn a completed criterion into a cross.
  const reviews = wave.reviews.filter(
    (review) => review.status !== 'Cancelled' && review.status !== 'Interrupted' && review.status !== 'Superseded',
  );
  if (reviews.length === 0) return [];
  const byIndex = new Map<number, TicketDetailReviewCriterion[]>();
  for (const review of reviews) {
    for (const row of review.criteria) {
      if (row.index <= 0) continue;
      const rows = byIndex.get(row.index) ?? [];
      rows.push(row);
      byIndex.set(row.index, rows);
    }
  }

  return [...byIndex.entries()]
    .sort(([left], [right]) => left - right)
    .map(([index, rows]) => {
      const status: AcceptanceCriterionSummary['status'] =
        rows.length === reviews.length && rows.every((row) => row.status === 'met')
          ? 'met'
          : rows.some((row) => row.status === 'not-met')
            ? 'not-met'
            : rows.some((row) => row.status === 'unproven')
              ? 'unproven'
              : rows.some((row) => row.status === 'not-checked')
                ? 'not-checked'
                : 'not-assessed';
      const source = rows.find((row) => row.status === status);
      return {
        index,
        criterion: rows[0]!.criterion,
        status,
        note: source?.note ?? null,
        priorAttemptId: source?.priorAttemptId ?? null,
      };
    });
}

export function AcceptanceCriteriaSummary({ wave }: { wave: ReviewWave | undefined }) {
  const criteria = summariseAcceptanceCriteria(wave);
  const met = criteria.filter((row) => row.status === 'met').length;
  return (
    <section className="review-merge-criteria" aria-labelledby="review-merge-criteria-title">
      <div className="review-merge-criteria-head">
        <h3 id="review-merge-criteria-title">Acceptance criteria</h3>
        {criteria.length > 0 ? (
          <span className="dim">
            {met} of {criteria.length} met
          </span>
        ) : null}
      </div>
      {criteria.length > 0 ? (
        <ol className="review-merge-criteria-list">
          {criteria.map((row) => (
            <li key={row.index} className={`is-${row.status}`}>
              <span className="review-merge-criteria-mark" aria-hidden>
                {row.status === 'met' ? '✓' : row.status === 'not-met' ? '✕' : '—'}
              </span>
              <span>
                <span className={row.status === 'met' || row.status === 'not-met' ? 'visually-hidden' : 'review-merge-criteria-note dim'}>
                  {CRITERION_STATUS_LABEL[row.status]}: </span>
                <span className="review-merge-criteria-text">{row.criterion}</span>
                {row.status !== 'met' && row.note ? (
                  <span className="review-merge-criteria-note dim">{row.note}</span>
                ) : null}
              </span>
            </li>
          ))}
        </ol>
      ) : (
        <p className="review-merge-criteria-empty dim">No acceptance criteria were reported for this head.</p>
      )}
    </section>
  );
}

/**
 * Review. Opens over the drawer from a Human Review ticket and puts everything a person
 * should look at before signing off in one place: whether the merge guard would let this
 * through right now, the screenshots the implementer captured, and what the AI reviewers
 * decided.
 *
 * It used to end in a "Merge in GitLab" button: a synchronous merge at this exact head that
 * fails closed. That button went dead in precisely the case a person most wanted a way
 * forward (a conflict), while the drawer's Approve button, which rebases, repairs conflicts
 * and re-reviews as needed, sat one layer down. Two ways to land a ticket was one too many;
 * the panel now only informs, and Approve on the drawer is the action.
 */
export function ReviewMergePanel({
  detail,
  now,
  onCancel,
}: {
  detail: TicketDetail;
  now: number;
  onCancel: () => void;
}) {
  const key = detail.ticket.ticketKey;
  const [readiness, setReadiness] = useState<TicketMergeReadiness | null>(null);
  const [readinessError, setReadinessError] = useState<string | null>(null);
  const guardRef = useRef<HTMLElement>(null);

  useLayoutEffect(() => {
    const guard = guardRef.current;
    if (!guard) return;
    const columns = Array.from(guard.children) as HTMLElement[];
    let lastWidth = -1;
    function balance() {
      if (!guard) return;
      const width = guard.clientWidth;
      lastWidth = width;
      // The narrow-screen stylesheet stacks the sections, independent of this ratio.
      if (getComputedStyle(guard).gridTemplateColumns.split(' ').length !== 2) return;
      let bestRatio = 0.4;
      let bestHeight = Infinity;
      let bestDifference = Infinity;
      // Measure actual wrapping (including notes and UI zoom), not character counts.
      // Prefer 40/60 on ties and keep either column wide enough to read comfortably.
      for (let step = 0; step <= 14; step += 1) {
        const ratio = 0.3 + step * 0.025;
        guard.style.setProperty('--guard-columns', `minmax(240px, ${ratio}fr) minmax(240px, ${1 - ratio}fr)`);
        const [left, right] = columns.map((column) => column.getBoundingClientRect().height);
        const height = Math.max(left!, right!);
        const difference = Math.abs(left! - right!);
        if (
          height < bestHeight - 0.5 ||
          (Math.abs(height - bestHeight) <= 0.5 &&
            (difference < bestDifference - 0.5 ||
              (Math.abs(difference - bestDifference) <= 0.5 &&
                Math.abs(ratio - 0.4) < Math.abs(bestRatio - 0.4))))
        ) {
          bestRatio = ratio;
          bestHeight = height;
          bestDifference = difference;
        }
      }
      guard.style.setProperty('--guard-columns', `minmax(240px, ${bestRatio}fr) minmax(240px, ${1 - bestRatio}fr)`);
    }
    balance();
    const observer = new ResizeObserver(() => {
      if (guard.clientWidth !== lastWidth) balance();
    });
    observer.observe(guard);
    // A viewport breakpoint can change without changing the capped panel width.
    window.addEventListener('resize', balance);
    let active = true;
    void document.fonts.ready.then(() => { if (active) balance(); });
    return () => {
      active = false;
      observer.disconnect();
      window.removeEventListener('resize', balance);
    };
  }, [readiness, readinessError, detail.reviews]);

  useEffect(() => {
    let active = true;
    setReadiness(null);
    setReadinessError(null);
    void api
      .mergeReadiness(key)
      .then((next) => {
        if (active) setReadiness(next);
      })
      .catch((error) => {
        if (active) setReadinessError(error instanceof Error ? error.message : String(error));
      });
    return () => {
      active = false;
    };
  }, [key, detail.ticket.version]);

  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        event.preventDefault();
        onCancel();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel]);

  const waves = groupReviewWaves(detail.reviews);
  const shots = detail.artifacts.filter((row) => isEvidenceImage(row.path));
  const freshShots = shots.filter((row) => !row.stale);
  const staleShots = shots.filter((row) => row.stale);
  const files = detail.artifacts.filter(
    (row) => !isEvidenceImage(row.path) && !HIDDEN_ARTIFACT_KINDS.has(row.kind) && !row.stale,
  );

  const loading = readiness === null && readinessError === null;
  const conflictsOnly =
    readiness !== null &&
    !readiness.ok &&
    readiness.checks.length > 0 &&
    readiness.checks.every((check) => check.ok || check.id === 'conflicts');
  const headline = readinessError
    ? 'Could not check merge readiness.'
    : loading
      ? 'Checking the merge guard…'
      : readiness?.ok
        ? 'The merge guard would let this through.'
        : readiness?.available
          ? conflictsOnly
            ? 'The branch conflicts with the target; Approve will rebase and repair it.'
            : 'The merge guard would refuse this right now.'
          : 'Merge readiness is not available.';

  return createPortal(
    <>
      <div className="review-merge-scrim" role="presentation" onClick={onCancel} />
      <section
        className="review-merge rise"
        role="dialog"
        aria-modal="true"
        aria-labelledby="review-merge-title"
        data-ready={loading ? '0' : '1'}
      >
        <header className="review-merge-head">
          <div>
            <p className="drawer-section-kicker review-merge-kicker">Review</p>
            <h2 id="review-merge-title" className="review-merge-title">
              {key} <span className="dim">{detail.ticket.title}</span>
            </h2>
          </div>
          <button type="button" className="btn btn-ghost btn-sm" onClick={onCancel} autoFocus>
            Close
          </button>
        </header>

        <div className="review-merge-body scroll-y">
          <section ref={guardRef} className={`review-merge-guard ${readiness?.ok ? 'is-ok' : loading ? '' : 'is-blocked'}`}>
            <div className="review-merge-guard-summary">
              <div className="review-merge-guard-head">
                {loading ? <Spinner size={14} /> : null}
                <strong>{headline}</strong>
                {readiness?.sha ? <span className="mono dim">at {readiness.sha.slice(0, 8)}</span> : null}
              </div>
              {readinessError ? <p className="review-merge-note is-bad">{readinessError}</p> : null}
              {readiness && !readiness.ok && readiness.reason ? (
                <p className="review-merge-note is-bad">{readiness.reason}</p>
              ) : null}
              {readiness?.headMoved ? (
                <p className="review-merge-note is-warn">
                  The MR head has moved since the last attempt pinned it. Evidence below may be for an older
                  commit; CI and review re-run on the new head before the guard passes.
                </p>
              ) : null}
              {readiness && readiness.checks.length > 0 ? (
                <ul className="review-merge-checks">
                  {readiness.checks.map((check) => (
                    <li key={check.id} className={check.ok ? 'is-ok' : 'is-bad'}>
                      <span className="review-merge-check-mark" aria-hidden>
                        {check.ok ? '✓' : '✕'}
                      </span>
                      <span className="review-merge-check-label">{check.label}</span>
                      <span className="review-merge-check-note dim">{check.note}</span>
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
            <AcceptanceCriteriaSummary wave={waves[0]} />
          </section>

          <section>
            <h3 className="drawer-section-kicker">Agent evidence</h3>
            {freshShots.length === 0 && files.length === 0 ? (
              <p className="review-merge-empty dim">
                No screenshots or files were captured at the current head.
                {staleShots.length > 0 ? ' Older captures are below.' : ''}
              </p>
            ) : null}
            {freshShots.length > 0 ? <ShotGrid shots={freshShots} /> : null}
            {files.length > 0 ? (
              <ul className="review-merge-files">
                {files.map((row) => (
                  <li key={row.id}>
                    <strong>{row.kind}</strong>
                    <span className="mono dim"> {row.path ? fileName(row.path) : row.ref}</span>
                    {row.path ? (
                      <a className="dim" href={`/api/artifacts/${row.id}/file`} target="_blank" rel="noreferrer">
                        open
                      </a>
                    ) : null}
                  </li>
                ))}
              </ul>
            ) : null}
            {staleShots.length > 0 ? (
              <details className="review-merge-stale">
                <summary className="dim">
                  {staleShots.length} older {staleShots.length === 1 ? 'capture' : 'captures'} from a previous head
                </summary>
                <ShotGrid shots={staleShots} />
              </details>
            ) : null}
          </section>

          <section>
            <h3 className="drawer-section-kicker">AI review</h3>
            <p className="review-merge-wave-summary">{summariseWave(waves[0])}</p>
            {waves.length === 0 ? null : (
              <div className="review-merge-reviews">
                {waves[0]!.reviews.map((review) => (
                  <ReviewCard key={review.attemptId} review={review} now={now} compact />
                ))}
              </div>
            )}
            {waves.slice(1).map((wave, index) => (
              <ReviewWaveSection key={wave.sha ?? `older-${index}`} wave={wave} now={now} open={false} compact />
            ))}
          </section>
        </div>

        <footer className="review-merge-foot">
          <p className="dim review-merge-fineprint">
            Nothing here merges. When you are satisfied, close this and press <strong>Approve</strong> on the
            ticket: Nightshift rebases the branch, sends a conflict to an agent for repair, waits for CI,
            re-reviews if the diff changed, and merges at the head the guard accepts.
          </p>
          <div className="review-merge-actions">
            <button type="button" className="btn btn-ghost" onClick={onCancel}>
              Close
            </button>
          </div>
        </footer>
      </section>
    </>,
    document.body,
  );
}

function ShotGrid({ shots }: { shots: TicketDetailArtifact[] }) {
  return (
    <ul className="review-merge-shots">
      {shots.map((row) => (
        <li key={row.id} className={row.stale ? 'is-stale' : undefined}>
          <a href={`/api/artifacts/${row.id}/file`} target="_blank" rel="noreferrer">
            <EvidenceImage artifactId={row.id} alt={row.ref ?? fileName(row.path)} expiredAt={row.fileExpiredAt} lazy />
          </a>
          <figcaption>
            {row.ref ?? fileName(row.path)}
            {row.stale ? <span className="drawer-stale-mark"> Stale</span> : null}
          </figcaption>
        </li>
      ))}
    </ul>
  );
}
